import type { ActivitySummary } from '@core/types/api/ActivitySummary/ActivitySummary';
import type { AdjustedPricing } from '@core/types/api/AdjustedPricing/AdjustedPricing';
import type { CompanySummary } from '@core/types/api/CompanySummary/CompanySummary';
import type { CompetitorSummary } from '@core/types/api/CompetitorSummary/CompetitorSummary';
import type { ContactSummary } from '@core/types/api/ContactSummary/ContactSummary';
import Enums from '@core/types/api/Enums';
import type { FixedPricing } from '@core/types/api/FixedPricing/FixedPricing';
import type { ImportedPricing } from '@core/types/api/ImportedPricing/ImportedPricing';
import type { MaterialSummary } from '@core/types/api/MaterialSummary/MaterialSummary';
import type { MixSummary } from '@core/types/api/MixSummary/MixSummary';
import type { PlantSummary } from '@core/types/api/PlantSummary/PlantSummary';
import type { ProductSummary } from '@core/types/api/ProductSummary/ProductSummary';
import type { ProjectConfigSummary } from '@core/types/api/ProjectConfigSummary/ProjectConfigSummary';
import type { ProjectSummary } from '@core/types/api/ProjectSummary/ProjectSummary';
import type { QuoteConfigSummary } from '@core/types/api/QuoteConfigSummary/QuoteConfigSummary';
import type { QuoteSummary } from '@core/types/api/QuoteSummary/QuoteSummary';
import type { ReminderSystemSettingSummary } from '@core/types/api/ReminderSystemSettingSummary/ReminderSystemSettingSummary';
import type { TruckingTypeSummary } from '@core/types/api/TruckingTypeSummary/TruckingTypeSummary';

import type { FilterByOpts, NamedFilterOpts } from '../../utils/ApiClient';
import type { ArchiveKey } from './useArchiveManager';

export type Filters = {
  'POST archive activity by ID': FilterByOpts<ActivitySummary>[];
  'POST archive material by ID': FilterByOpts<MaterialSummary>[];
  'POST archive product by ID': FilterByOpts<ProductSummary>[];
  'POST archive contact by ID': FilterByOpts<ContactSummary>[];
  'POST archive company by ID': FilterByOpts<CompanySummary>[];
  'POST archive mix by ID': FilterByOpts<MixSummary>[];
  'POST archive project by ID': NamedFilterOpts<ProjectSummary>[];
  'POST archive quote by ID': NamedFilterOpts<QuoteSummary>[];
  'POST archive quote config by ID': NamedFilterOpts<QuoteConfigSummary>[];
  'POST archive project config by ID': NamedFilterOpts<ProjectConfigSummary>[];
  'POST archive trucking type by ID': FilterByOpts<TruckingTypeSummary>[];
  'POST archive reminder system setting by ID': FilterByOpts<ReminderSystemSettingSummary>[];
  'POST archive plant by ID': FilterByOpts<PlantSummary>[];
  'POST archive competitor by ID': FilterByOpts<CompetitorSummary>[];
  'POST archive fixed pricing by ID': FilterByOpts<FixedPricing>[];
  'POST archive adjusted pricing by ID': FilterByOpts<AdjustedPricing>[];
  'POST archive imported pricing by ID': FilterByOpts<ImportedPricing>[];
};

interface ArchivedEntityProps<T extends ArchiveKey> {
  subjectName: string;
  hideArchivedFilterBy: Filters[T];
}

export const ARCHIVED_ENTITY_MAP: {
  [K in ArchiveKey]: ArchivedEntityProps<K>;
} = {
  'POST archive activity by ID': {
    subjectName: 'Activity',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive material by ID': {
    subjectName: 'Material',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive product by ID': {
    subjectName: 'Product',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive contact by ID': {
    subjectName: 'Contact',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive company by ID': {
    subjectName: 'Company',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive mix by ID': {
    subjectName: 'Mix',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive quote by ID': {
    subjectName: 'Quote',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive quote config by ID': {
    subjectName: 'Quote Config',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive project by ID': {
    subjectName: 'Project',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive project config by ID': {
    subjectName: 'Project Config',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive trucking type by ID': {
    subjectName: 'Trucking Type',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive reminder system setting by ID': {
    subjectName: 'System Reminder',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive competitor by ID': {
    subjectName: 'Competitor',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive plant by ID': {
    subjectName: 'Location',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive fixed pricing by ID': {
    subjectName: 'Fixed Pricing',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive adjusted pricing by ID': {
    subjectName: 'Tier Pricing',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
  'POST archive imported pricing by ID': {
    subjectName: 'Price List',
    hideArchivedFilterBy: [
      { name: 'isArchived', operation: Enums.FilterOperation.Equals, values: [false] },
    ],
  },
};
